import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  Capabilities,
  Channel,
  GuestSession,
  HostInit,
  RegistryResponse,
  WidgetDelivery,
  WidgetInstance,
  WidgetResolution,
} from '@resto/contracts';
import {
  BundledWidgetProvider,
  RemoteWidgetProvider,
  checkHostCompatibility,
  mountWidget,
  resolveWidget,
  verifyManifestSignature,
  type MountedWidget,
} from '@resto/widget-sdk';
import { BUNDLED_REGISTRY, BUNDLED_VERSIONS } from './widgets.generated';
import { isNativeHost, onNativeMessage, sendToNative } from './native';
import { maxBackButton, maxOpenLink, maxReady } from './max';
import { AbsoluteWidgetsProvider, LocalWidgetsProvider, type WidgetProviders } from './providers';
import { apiBaseForWidget, createGuestSession, describeApiError, loadRegistry, refreshGuestSession, sendWidgetEvent } from './api';
import { currentTable } from './table';
import { WIDGET_MANIFEST_KEY_ID, WIDGET_MANIFEST_PUBLIC_KEY } from './manifest-key';

const HOST_VERSION = import.meta.env.VITE_HOST_VERSION ?? '0.1.0';
const VENUE_ID = import.meta.env.VITE_DEFAULT_VENUE_ID ?? 'venue-1';
const CHANNEL = (import.meta.env.VITE_GUEST_CHANNEL ?? 'tma') as Channel;
const WIDGETS_BASE_URL = import.meta.env.VITE_WIDGETS_BASE_URL;

type ToastLevel = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  level: ToastLevel;
}

interface BootError {
  message: string;
  hint: string;
}

function readDeliveryMode(): WidgetDelivery {
  const value = new URLSearchParams(window.location.search).get('delivery');
  return value === 'bundled' ? 'bundled' : 'remote';
}

const SKIP_REASONS: Record<string, string> = {
  channel: 'канал не поддерживается',
  capability: 'нет возможности коннектора',
  host_version: 'старая версия хоста',
  disabled: 'выключен в бэк-офисе',
  delivery: 'недоступен способ доставки',
};

export function App() {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [session, setSession] = useState<GuestSession | null>(null);
  const [registry, setRegistry] = useState<RegistryResponse | null>(null);
  const [bootError, setBootError] = useState<BootError | null>(null);
  const [selected, setSelected] = useState<WidgetInstance | null>(null);
  const [embed] = useState(() => new URLSearchParams(window.location.search).has('embed'));
  const [mode] = useState<WidgetDelivery>(readDeliveryMode);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [debug] = useState(() => new URLSearchParams(window.location.search).has('debug'));

  // MAX Bridge: сообщаем клиенту, что экран готов, и отдаём системную «Назад».
  useEffect(() => {
    maxReady();
  }, []);

  useEffect(() => {
    if (!selected) return maxBackButton(false, () => undefined);
    return maxBackButton(true, () => setSelected(null));
  }, [selected]);
  // Стол берём из QR один раз: дальше он живёт в init и уходит в заказ.
  const [tableId] = useState<string | null>(() => currentTable());

  // Прямая ссылка на виджет: ?widget=delivery — используется в предпросмотре бэкофиса.
  useEffect(() => {
    if (!registry || selected) return;
    const wanted = new URLSearchParams(window.location.search).get('widget');
    if (!wanted) return;
    const match = registry.widgets.find((widget) => widget.manifest.id === wanted);
    if (match) setSelected(match);
  }, [registry, selected]);

  const addToast = useCallback((message: string, level: ToastLevel = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current.slice(-3), { id, message, level }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4500);
  }, []);

  const boot = useCallback(async () => {
    try {
      const guest = await createGuestSession(VENUE_ID, CHANNEL);
      const nextRegistry = await loadRegistry(guest.token, CHANNEL, HOST_VERSION);
      // Подпись обязательна: неподписанный или подменённый манифест не грузим.
      const widgets: WidgetInstance[] = [];
      const rejected: string[] = [];
      for (const widget of nextRegistry.widgets) {
        const valid = await verifyManifestSignature(
          widget.manifest,
          WIDGET_MANIFEST_PUBLIC_KEY,
          WIDGET_MANIFEST_KEY_ID,
        );
        if (valid) widgets.push(widget);
        else rejected.push(widget.manifest.id);
      }
      if (rejected.length > 0) {
        console.error('виджеты с неверной подписью манифеста:', rejected.join(', '));
        addToast(`Не загружены виджеты без подписи: ${rejected.join(', ')}`, 'error');
      }
      setSession(guest);
      setRegistry({ ...nextRegistry, widgets });
      setStatus('ready');
    } catch (error) {
      setBootError(describeApiError(error));
      setStatus('error');
    }
  }, [addToast]);

  useEffect(() => {
    // boot() трогает состояние после ответа API; выносим старт из тела эффекта,
    // чтобы не ловить react-hooks/set-state-in-effect.
    const timer = window.setTimeout(() => {
      void boot();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [boot]);

  const retryBoot = (): void => {
    setStatus('loading');
    setSelected(null);
    void boot();
  };

  const providers = useMemo(() => {
    // Разрешены свой origin и домен платформы; env дополняет список для dev.
    const allowlist = [
      window.location.hostname,
      'resto.hermes-go.com',
      ...(import.meta.env.VITE_WIDGETS_ALLOWLIST ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ];
    const remote = new RemoteWidgetProvider({ allowlist });
    return {
      remote: WIDGETS_BASE_URL ? new LocalWidgetsProvider(remote, WIDGETS_BASE_URL) : remote,
      bundled: new AbsoluteWidgetsProvider(new BundledWidgetProvider(BUNDLED_REGISTRY)),
    };
  }, []);

  const baseInit = useMemo<Omit<HostInit, 'widgetId'> | null>(() => {
    if (!session) return null;
    const apiOrigin = new URLSearchParams(window.location.search).get('apiOrigin');
    return {
      venueId: session.venue.id,
      channel: CHANNEL,
      guestToken: session.token,
      apiBaseUrl: apiOrigin ? `${apiOrigin.replace(/\/$/, '')}/api/v1` : apiBaseForWidget(),
      locale: 'ru',
      theme: 'light',
      config: {},
      hostVersion: HOST_VERSION,
      ...(tableId ? { tableId } : {}),
    };
  }, [session, tableId]);

  return (
    <div className={embed ? 'app app--embed' : 'app'}>
      <main className="app__main">
        {status === 'loading' && (
          <section className="screen">
            <div className="loader">
              <span className="loader__ring" aria-hidden="true" />
              <h1>Открываем меню</h1>
              <p>Подключаемся к ресторану…</p>
            </div>
          </section>
        )}

        {status === 'error' && (
          <section className="screen">
            <div className="fatal">
              <span className="fatal__icon" aria-hidden="true">!</span>
              <h1>Не удалось загрузить</h1>
              <p className="fatal__message">Проверьте связь и попробуйте ещё раз.</p>
              {debug && bootError && <p className="fatal__hint">{bootError.message}</p>}
              <div className="fatal__actions">
                <button type="button" className="btn btn--primary" onClick={retryBoot}>
                  Повторить
                </button>
              </div>
            </div>
          </section>
        )}

        {status === 'ready' && registry && baseInit && (
          selected ? (
            <WidgetStage
              widget={selected}
              baseInit={baseInit}
              capabilities={registry.capabilities}
              mode={mode}
              providers={providers}
              onBack={() => setSelected(null)}
              onToast={addToast}
            />
          ) : (
            <HomeScreen
              session={session}
              widgets={registry.widgets}
              onOpen={setSelected}
              debug={debug}
              registry={registry}
            />
          )
        )}
      </main>

      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.level}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

const WIDGET_ORDER = ['ai-chat', 'delivery', 'dine-in', 'booking', 'loyalty', 'services', 'rental'];

const WIDGET_UI: Record<string, { label: string; icon: IconName; hint?: string }> = {
  'ai-chat': { label: 'AI консультант', icon: 'sparkles' },
  delivery: { label: 'Доставка', icon: 'bike' },
  'dine-in': { label: 'В зале', icon: 'qr' },
  booking: { label: 'Бронь', icon: 'calendar' },
  loyalty: { label: 'Бонусы', icon: 'gift' },
  services: { label: 'Услуги', icon: 'spa' },
  rental: { label: 'Прокат', icon: 'clock' },
};

const WIDGET_DESCRIPTION: Record<string, string> = {
  'ai-chat': 'Подберёт блюдо и ответит на вопросы',
  delivery: 'Доставка и самовывоз',
  'dine-in': 'Заказ по QR за столом',
  booking: 'Забронировать стол',
  loyalty: 'Баллы и подарки',
  services: 'Сауны, СПА и фитнес',
  rental: 'Прокат с оплатой по часам',
};

function HomeScreen({
  session,
  widgets,
  onOpen,
  debug,
  registry,
}: {
  session: GuestSession | null;
  widgets: WidgetInstance[];
  onOpen: (widget: WidgetInstance) => void;
  debug: boolean;
  registry: RegistryResponse;
}) {
  const byId = useMemo(() => new Map(widgets.map((widget) => [widget.manifest.id, widget])), [widgets]);
  const quick = WIDGET_ORDER.map((id) => byId.get(id)).filter((widget): widget is WidgetInstance => Boolean(widget)).slice(0, 4);
  const quickIds = new Set(quick.map((widget) => widget.manifest.id));
  const rest = widgets.filter((widget) => !quickIds.has(widget.manifest.id));
  const firstMenu = byId.get('delivery') ?? byId.get('dine-in') ?? widgets[0] ?? null;

  const open = (id: string): void => {
    const widget = byId.get(id);
    if (widget) onOpen(widget);
  };

  return (
    <div className="home">
      <header className="home__top">
        <button type="button" className="venue" onClick={() => open('loyalty')} aria-label="Профиль и бонусы">
          <span className="venue__avatar" aria-hidden="true">{session?.venue.name.slice(0, 1) ?? 'R'}</span>
          <span className="venue__text">
            <span className="venue__greeting">{greeting()}</span>
            <strong>{session?.venue.name ?? 'Resto'}</strong>
          </span>
        </button>
        <button type="button" className="icon-btn" onClick={() => open('dine-in')} aria-label="Сканировать QR-код столика">
          <Icon name="qr" />
        </button>
      </header>

      <button type="button" className="search" onClick={() => firstMenu && onOpen(firstMenu)}>
        <Icon name="search" />
        <span>Найти блюдо</span>
        <span className="search__filter" aria-hidden="true"><Icon name="sliders" /></span>
      </button>

      <section className="hero-card">
        <span className="hero-card__kicker">Resto</span>
        <h1>Что закажем сегодня?</h1>
        <p>Меню, доставка и заказ в зале — в одном месте.</p>
        <button type="button" className="btn btn--primary" onClick={() => firstMenu && onOpen(firstMenu)}>
          Открыть меню
          <Icon name="arrow" />
        </button>
      </section>

      {quick.length > 0 && (
        <div className="quick" role="list">
          {quick.map((widget) => {
            const ui = WIDGET_UI[widget.manifest.id] ?? { label: widget.manifest.name, icon: 'grid' as const };
            const highlighted = widget.manifest.id === 'ai-chat';
            return (
              <button
                key={widget.manifest.id}
                type="button"
                role="listitem"
                className={`quick__item${highlighted ? ' quick__item--ai' : ''}`}
                onClick={() => onOpen(widget)}
              >
                <span className="quick__icon">
                  <Icon name={ui.icon} />
                  {ui.hint && <span className="quick__hint">{ui.hint}</span>}
                </span>
                <span className="quick__label">{ui.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {rest.length > 0 && <div className="section-head"><h2>Ещё в приложении</h2></div>}

      {widgets.length === 0 ? (
        <div className="empty">
          <h3>Пока ничего не подключено</h3>
          <p>Ресторан ещё настраивает разделы.</p>
        </div>
      ) : rest.length > 0 ? (
        <div className="services">
          {rest.map((widget) => {
            const ui = WIDGET_UI[widget.manifest.id] ?? { label: widget.manifest.name, icon: 'grid' as const };
            const description = WIDGET_DESCRIPTION[widget.manifest.id] ?? widget.manifest.description;
            return (
              <button key={widget.manifest.id} type="button" className="service" onClick={() => onOpen(widget)}>
                <span className="service__icon"><Icon name={ui.icon} /></span>
                <span className="service__text">
                  <strong>{ui.label}</strong>
                  <span>{description}</span>
                </span>
                <Icon name="chevron" />
              </button>
            );
          })}
        </div>
      ) : null}

      {debug && <DeveloperPanel registry={registry} />}
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'Доброй ночи';
  if (hour < 12) return 'Доброе утро';
  if (hour < 18) return 'Добрый день';
  return 'Добрый вечер';
}

type IconName = 'sparkles' | 'bike' | 'qr' | 'calendar' | 'gift' | 'spa' | 'clock' | 'search' | 'sliders' | 'arrow' | 'back' | 'chevron' | 'grid';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    sparkles: (
      <>
        <path d="M12 3l1.7 4.5L18 9l-4.3 1.5L12 15l-1.7-4.5L6 9l4.3-1.5L12 3Z" />
        <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8Z" />
      </>
    ),
    bike: (
      <>
        <circle cx="6" cy="17" r="3.2" />
        <circle cx="18" cy="17" r="3.2" />
        <path d="M9 17h4l3-7h2M14 10l-2-3H9M12 10h5" />
      </>
    ),
    qr: (
      <>
        <rect x="3" y="3" width="6" height="6" rx="1.2" />
        <rect x="15" y="3" width="6" height="6" rx="1.2" />
        <rect x="3" y="15" width="6" height="6" rx="1.2" />
        <path d="M15 15h2v2h-2zM19 19h2v2h-2zM15 19h2M19 15h2" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M7 3v4M17 3v4M3 10h18M8 14h3M8 17h7" />
      </>
    ),
    gift: (
      <>
        <rect x="4" y="9" width="16" height="12" rx="2.5" />
        <path d="M3 9h18M12 9v12M12 9c-1-4-6-4-6-1 0 2 3 2 6 1ZM12 9c1-4 6-4 6-1 0 2-3 2-6 1Z" />
      </>
    ),
    spa: (
      <>
        <path d="M12 20c-4 0-7-3-7-7 4 0 7 2 7 7Z" />
        <path d="M12 20c4 0 7-3 7-7-4 0-7 2-7 7Z" />
        <path d="M12 20V9c0-3 2-5 4-6" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l4 4" />
      </>
    ),
    sliders: (
      <>
        <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
        <circle cx="16" cy="7" r="2" />
        <circle cx="8" cy="17" r="2" />
      </>
    ),
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    back: <path d="M15 18l-6-6 6-6" />,
    chevron: <path d="M9 6l6 6-6 6" />,
    grid: (
      <>
        <rect x="4" y="4" width="6" height="6" rx="1.4" />
        <rect x="14" y="4" width="6" height="6" rx="1.4" />
        <rect x="4" y="14" width="6" height="6" rx="1.4" />
        <rect x="14" y="14" width="6" height="6" rx="1.4" />
      </>
    ),
  };

  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

function WidgetStage({
  widget,
  baseInit,
  capabilities,
  mode,
  providers,
  onBack,
  onToast,
}: {
  widget: WidgetInstance;
  baseInit: Omit<HostInit, 'widgetId'>;
  capabilities: Capabilities;
  mode: WidgetDelivery;
  providers: WidgetProviders;
  onBack?: () => void;
  onToast: (message: string, level?: ToastLevel) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const full = widget.manifest.id === 'ai-chat';
  const [, setResolution] = useState<WidgetResolution | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [scoped, setScoped] = useState<GuestSession | null>(null);
  const scopedRef = useRef<GuestSession | null>(null);
  const mountedRef = useRef<MountedWidget | null>(null);
  const [loading, setLoading] = useState(true);

  // Сессия выдаётся под конкретный виджет: по ней API решает, отдавать ли
  // адрес доставки. Токен хоста виджету не передаём.
  useEffect(() => {
    let alive = true;
    createGuestSession(baseInit.venueId, baseInit.channel, widget.manifest.id)
      .then((guest) => {
        if (!alive) return;
        scopedRef.current = guest;
        setScoped(guest);
      })
      .catch((error) => {
        if (alive) setScopeError(describeApiError(error).message);
      });
    return () => {
      alive = false;
    };
  }, [baseInit.venueId, baseInit.channel, widget.manifest.id]);

  // Access-токен живёт минуты: продлеваем заранее и отдаём виджету новый.
  useEffect(() => {
    if (!scoped) return;
    let stopped = false;
    let timer = 0;

    const schedule = (): void => {
      const current = scopedRef.current;
      if (!current || stopped) return;
      const expiresAt = new Date(current.expiresAt).getTime();
      const delay = Math.max(5000, expiresAt - Date.now() - 60_000);
      timer = window.setTimeout(() => {
        void refresh();
      }, delay);
    };

    const refresh = async (): Promise<void> => {
      const current = scopedRef.current;
      if (!current?.refreshToken || stopped) return;
      try {
        const next = await refreshGuestSession(current.refreshToken);
        if (stopped) return;
        scopedRef.current = next;
        mountedRef.current?.setToken(next.token, next.expiresAt);
        schedule();
      } catch (error) {
        if (!stopped) setScopeError(describeApiError(error).message);
      }
    };

    schedule();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [scoped]);

  const recoverSession = useCallback(
    async (reason: string): Promise<void> => {
      const current = scopedRef.current;
      if (current?.refreshToken) {
        try {
          const next = await refreshGuestSession(current.refreshToken);
          scopedRef.current = next;
          setScoped(next);
          mountedRef.current?.setToken(next.token, next.expiresAt);
          onToast('Сессия продлена', 'success');
          return;
        } catch {
          // Ниже — нативный фолбэк или понятная ошибка.
        }
      }
      if (isNativeHost()) {
        sendToNative('auth-required', { reason });
        return;
      }
      setScopeError('Сессия истекла. Откройте виджет заново.');
    },
    [onToast],
  );

  const init = useMemo<HostInit | null>(
    () =>
      scoped
        ? {
            ...baseInit,
            guestToken: scoped.token,
            widgetId: widget.manifest.id,
            config: widget.config,
          }
        : null,
    [baseInit, widget, scoped],
  );

  useEffect(
    () =>
      onNativeMessage((message) => {
        if (message.type === 'payment-result') {
          const status = String(message.payload?.status ?? 'unknown');
          if (status === 'success') {
            onToast('Оплата прошла', 'success');
          } else if (status === 'pending') {
            onToast('Ожидаем подтверждение оплаты', 'info');
          } else {
            onToast('Оплата не прошла', 'error');
          }
        }
      }),
    [onToast],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!init) return;

    let disposed = false;
    let mounted: MountedWidget | null = null;

    setLoading(true);
    setStageError(null);
    setResolution(null);

    void (async () => {
      try {
        const compatibility = checkHostCompatibility(widget.manifest, {
          channel: CHANNEL,
          hostVersion: HOST_VERSION,
          capabilities,
        });
        if (!compatibility.ok) {
          throw new Error(`виджет несовместим: ${compatibility.details ?? compatibility.reason}`);
        }

        const nextResolution = await resolveWidget(widget.manifest, {
          channel: CHANNEL,
          hostVersion: HOST_VERSION,
          capabilities,
          deliveries: { [CHANNEL]: mode },
          providers,
        });
        if (disposed) return;

        setResolution(nextResolution);
        mounted = mountWidget({
          container,
          resolution: nextResolution,
          init,
          readyTimeoutMs: 10000,
          fill: full,
          onReady: () => {
            setLoading(false);
            if (mounted) {
              mountedRef.current = mounted;
              mounted.setTheme(init.theme);
            }
            sendWidgetEvent(init.guestToken, {
              widgetId: widget.manifest.id,
              eventName: 'widget.open',
              version: widget.manifest.version,
              payload: { delivery: mode },
            });
          },
          onToast: (message, level) => onToast(message, level),
          onNavigate: (url) => {
            if (isNativeHost()) {
              sendToNative('navigate', { url });
              return;
            }
            maxOpenLink(url);
          },
          onAuthRequired: (reason) => {
            void recoverSession(reason);
          },
          onPayment: (orderId, amount, details) => {
            if (isNativeHost()) {
              sendToNative('payment', {
                orderId,
                amount,
                guestToken: init.guestToken,
                apiBaseUrl: init.apiBaseUrl,
                ...(details.paymentId ? { paymentId: details.paymentId } : {}),
                ...(details.confirmationUrl
                  ? { confirmationUrl: details.confirmationUrl }
                  : {}),
              });
              return;
            }
            if (details.confirmationUrl) {
              maxOpenLink(details.confirmationUrl);
              return;
            }
            onToast(
              `Оплата заказа ${orderId.slice(0, 8)} на ${(amount.amount / 100).toFixed(0)} ₽ — ссылка не пришла`,
              'error',
            );
          },
          onError: (code, message) => {
            setLoading(false);
            sendWidgetEvent(init.guestToken, {
              widgetId: widget.manifest.id,
              eventName: 'widget.error',
              version: widget.manifest.version,
              payload: { code, message: message.slice(0, 300) },
            });
            setStageError(`${message} (${code})`);
            onToast(message, 'error');
          },
        });
      } catch (error) {
        if (disposed) return;
        setLoading(false);
        setStageError(error instanceof Error ? error.message : String(error));
      }
    })();

    return () => {
      disposed = true;
      mountedRef.current = null;
      mounted?.destroy();
    };
  }, [widget, init, capabilities, mode, providers, onToast, full, recoverSession]);

  return (
    <section className={`stage${full ? ' stage--full' : ''}`}>
      <div className="stage__bar">
        <button type="button" className="icon-btn icon-btn--ghost" onClick={onBack} aria-label="Назад">
          <Icon name="back" />
        </button>
        <div className="stage__title">
          <h1>{widget.manifest.name}</h1>
        </div>
      </div>

      {(stageError || scopeError) && (
        <div className="stage__error">
          <strong>Виджет не открылся</strong>
          <span>{stageError ?? scopeError}</span>
          <button type="button" className="btn btn--ghost" onClick={onBack}>
            Вернуться к списку
          </button>
        </div>
      )}

      <div className="stage__frame-wrap">
        {loading && <div className="stage__loading">Загружаем виджет…</div>}
        <div ref={containerRef} className="stage__frame" />
      </div>

    </section>
  );
}

function DeveloperPanel({ registry }: { registry: RegistryResponse }) {
  const capabilityEntries = Object.entries(registry.capabilities);
  return (
    <details className="dev">
      <summary>
        Для разработчика: скрытые виджеты ({registry.skipped.length}), возможности коннектора и
        сборка
      </summary>

      <div className="dev__grid">
        <div>
          <h3>Скрытые виджеты</h3>
          {registry.skipped.length === 0 ? (
            <p className="dev__muted">Все виджеты прошли фильтры.</p>
          ) : (
            <table className="dev__table">
              <thead>
                <tr>
                  <th>виджет</th>
                  <th>причина</th>
                  <th>детали</th>
                </tr>
              </thead>
              <tbody>
                {registry.skipped.map((entry) => (
                  <tr key={`${entry.widgetId}-${entry.reason}`}>
                    <td>{entry.widgetId}</td>
                    <td>
                      {entry.reason}
                      <span className="dev__muted"> — {SKIP_REASONS[entry.reason] ?? 'причина'}</span>
                    </td>
                    <td>{entry.details ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h3>Возможности коннектора</h3>
          <ul className="dev__caps">
            {capabilityEntries.length === 0 && <li className="dev__muted">API не вернул capabilities</li>}
            {capabilityEntries.map(([name, enabled]) => (
              <li key={name} className={enabled ? 'dev__cap--on' : 'dev__cap--off'}>
                <span>{name}</span>
                <span>{enabled ? 'есть' : 'нет'}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3>Bundled-сборка</h3>
          {Object.keys(BUNDLED_REGISTRY).length === 0 ? (
            <p className="dev__muted">Не собрана: запустите pnpm build:widgets.</p>
          ) : (
            <ul className="dev__caps">
              {Object.entries(BUNDLED_VERSIONS).map(([id, version]) => (
                <li key={id}>
                  <span>{id}</span>
                  <span>v{version}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="dev__foot">
        <span>канал: {registry.channel}</span>
        <span>hostVersion: {registry.hostVersion}</span>
        <span>объект: {registry.venueId}</span>
        <span>виджетов: {registry.widgets.length}</span>
      </div>
    </details>
  );
}
